// Extracted NotesCell component and CallLog interface from CallLogPage.

import { useState } from "react";
import type { JSX } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api.js";

type CallDirection = "inbound" | "outbound";
type CallStatus = "answered" | "missed" | "busy" | "no_answer" | "failed";

export interface CallLog {
  _id: string;
  patient_id: string | null;
  direction: CallDirection;
  status: CallStatus;
  caller_number: string;
  callee_number: string;
  duration_seconds: number | null;
  notes: string | null;
  recording_url: string | null;
  started_at: string;
  ended_at: string | null;
}

export function NotesCell({ log }: { log: CallLog }): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(log.notes ?? "");
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: () => apiFetch(`/call-logs/${log._id}/notes`, { method: "PATCH", body: JSON.stringify({ notes: draft }) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["call-logs"] }); setEditing(false); },
  });

  if (editing) {
    return (
      <div className="flex gap-2 items-center">
        <input
          className="border border-slate-300 rounded px-2 py-1 text-xs w-40"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />
        <button onClick={() => save.mutate()} className="text-xs text-navy-700 font-medium">Save</button>
        <button onClick={() => setEditing(false)} className="text-xs text-slate-400">Cancel</button>
      </div>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className="text-xs text-slate-500 cursor-pointer hover:text-slate-800 max-w-xs truncate block"
      title={log.notes ?? "Click to add notes"}
    >
      {log.notes ?? <span className="italic text-slate-300">add notes</span>}
    </span>
  );
}
